import { BackEndCodeLanguageType, ForkMode, ForkModeType } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Stack, StackItem, Text } from '@prophecy/ui-v3';
import { Alert } from '@prophecy/ui-v3/ui-compat';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useMixPanel } from '../../../context/mixpanel/context';
import { Entity } from '../../types/Entity';
import { EntityEvents } from '../../user-analytics';
import { useProjectCodeGenStatusMetadata } from '../CodeGeneration/hook';
import { ConflictResolutionProvider } from '../Conflict/Conflict';
import { useAllBranches } from '../git-hooks';
import { useIsProjectAdmin } from '../useIsProjectAdmin';
import { BranchSelection } from './BranchSelection';
import { CompareAndCommitFlow, CompareAndCommitFlowPanel } from './CompareAndCommitFlow';
import { StepProvider } from './StepContext';

export function CommitsTab({
  mainBranch,
  projectId,
  currentBranch,
  forkMode,
  isSqlProject,
  name
}: {
  mainBranch: string;
  language: BackEndCodeLanguageType;
  projectId: string;
  name: string;
  isSqlProject: boolean;
  forkMode: ForkModeType;
  currentBranch: string;
}) {
  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents[Entity.Project].metadata.prLandingStep, {
      projectId,
      currentBranch: currentBranch,
      baseBranch: mainBranch
    });
  }, [projectId, track, currentBranch, mainBranch]);

  const { isCodeGenerating } = useProjectCodeGenStatusMetadata(projectId, true);

  const { branches } = useAllBranches({
    projectId,
    currentBranch
  });

  const [searchParams] = useSearchParams();

  const [baseBranchName, setBaseBranch] = useState(mainBranch);
  const _branch = searchParams.get('branch') ?? currentBranch;

  const [currentBranchName, setBranch] = useState(_branch);

  // update the selected branch if the current branch changes
  useEffect(() => {
    if (_branch) setBranch(_branch);
  }, [_branch]);

  const { isProjectAdmin } = useIsProjectAdmin(projectId);
  const isOnMainBranch = currentBranchName === mainBranch;

  function onCurrentBranchChange(branch: string) {
    setBranch(branch);
  }

  return (
    <StepProvider projectId={projectId}>
      <ConflictResolutionProvider>
        <Stack gap={theme.spaces.x24} height='100%'>
          {isCodeGenerating && <Alert>Project is being updated. Please wait until the updates are complete.</Alert>}
          <Stack direction='horizontal' align='space-between' alignY='center'>
            <Text level='sm'>Commit changes</Text>

            <CompareAndCommitFlowPanel
              name={name}
              projectId={projectId}
              isSqlProject={isSqlProject}
              baseBranch={baseBranchName}
              mainBranch={mainBranch}
              currentBranch={currentBranchName}
              isProjectAdmin={isProjectAdmin}
              setBaseBranch={setBaseBranch}
              // when we are feature branch in forked project, we compare it with forked main
              // only in case we are in main branch of forked project, we compare it with upstream main
              forkMode={isOnMainBranch && forkMode === ForkMode.Multi ? ForkMode.Multi : ForkMode.Single}
            />
          </Stack>

          <BranchSelection
            projectId={projectId}
            mainBranch={mainBranch}
            currentBranch={currentBranchName}
            baseBranch={baseBranchName}
            branches={branches}
            onCurrentBranchChange={onCurrentBranchChange}
            onBaseBranchChange={setBaseBranch}
            forkMode={forkMode}
            isSqlProject={isSqlProject}
          />

          <StackItem grow='1'>
            <CompareAndCommitFlow
              key={`${currentBranchName}_${baseBranchName}`}
              projectId={projectId}
              isSqlProject={isSqlProject}
              baseBranch={baseBranchName}
              mainBranch={mainBranch}
              currentBranch={currentBranchName}
              // when we are feature branch in forked project, we compare it with forked main
              // only in case we are in main branch of forked project, we compare it with upstream main
              forkMode={isOnMainBranch && forkMode === ForkMode.Multi ? ForkMode.Multi : ForkMode.Single}
            />
          </StackItem>
        </Stack>
      </ConflictResolutionProvider>
    </StepProvider>
  );
}
